import { BlurFade } from "@/components/ui/blur-fade";
import Marquee from "@/components/ui/marquee";
import { useFeaturedClientLogos } from "@/hooks/use-clients";
import { WavyMark } from "@/components/ui/wavy-mark";
import { AnimatedHeadline } from "@/components/ui/animated-headline";
import { WorkshopDetailEyebrow } from "@/components/ui/workshop-detail-eyebrow";
import { HairlineTimeline, type HairlineStop } from "@/components/scarcity/HairlineTimeline";
import { requestCheckout } from "@/lib/open-checkout";

export interface SessionCard {
  id: number;
  startDatetime: string | Date;
  endDatetime: string | Date;
  timezone: string;
  /** Number of seats sold (for the 30-square availability bar). */
  seatsSold?: number | null;
  /** Total capacity (for percent calculation). */
  maxCapacity?: number | null;
  /** When true, render the tile with sold-out styling inline (not bookable). */
  soldOut?: boolean;
}

/** A purely informational sold-out card rendered in the Upcoming Dates strip.
 *  Kept for backward-compat call sites; new code should pass sessionCards with
 *  soldOut:true mixed in chronologically. */
export interface SoldOutCard {
  /** Display date label, e.g. "Sat, May 9" */
  dateLabel: string;
  /** Optional secondary line; defaults to "Sold out — see open dates" */
  subline?: string;
}


export interface WorkshopHeroProps {
  /** Eyebrow label, e.g. "FULL-DAY LIVE WORKSHOP" */
  eyebrow?: string;
  headline: string;
  subtitle?: string;
  /** Small label above the headline (series name when the headline is a pitch). */
  kicker?: string;
  /** Short labelled facts under the subtitle, e.g. { label: "Privacy", text: "…" }. */
  proofLines?: Array<{ label: string; text: string }>;
  /** Price shown directly under the subtitle, e.g. "From $559". Only set by
   *  the ?price=1 hero-price test; undefined keeps the page as it was. */
  priceLine?: string;
  /** Pre-discount price, struck through before priceLine (ad coupon). */
  priceStrike?: string;
  /** Info cards below headline (legacy, single-event mode) */
  cards?: Array<{ label: string; value: string; icon?: string }>;
  /** Series mode: 3 upcoming sessions, each clickable */
  sessionCards?: SessionCard[];
  /** Optional sold-out card to render before sessionCards in the Upcoming Dates strip. */
  soldOutCard?: SoldOutCard;
  selectedSessionId?: number | null;
  onSelectSession?: (id: number) => void;
  primaryCta?: { label: string; href: string };
  secondaryCta?: { label: string; href: string };
  className?: string;
  /**
   * Override the eyebrow row above the hero headline.
   * When provided, renders this node instead of the default WorkshopDetailEyebrow.
   * The caller is responsible for controlling visibility across breakpoints.
   * When omitted, the default WorkshopDetailEyebrow is rendered (hidden on mobile,
   * visible on sm+).
   */
  eyebrowNode?: React.ReactNode;
  /**
   * When true, the upcoming-dates session card strip is hidden on mobile viewports.
   * Useful for cowork where the CTA replaces the date strip on small screens.
   * NOTE: Ignored when `hairlineStops` is provided — the hairline timeline brings
   * its own mobile fallback (vertical stacked list).
   */
  hideSessionCardsMobile?: boolean;
  /**
   * Scarcity-display hairline timeline. When provided (and non-empty), the hero
   * renders the editorial hairline-thread timeline in place of the legacy
   * sessionCards strip. The `sessionCards` prop is then ignored for rendering,
   * though `onSelectSession` is still honored for tile clicks.
   * Design source: docs/brainstorms/2026-05-26-workshop-scarcity-display-requirements.md
   */
  hairlineStops?: HairlineStop[];
}

// Format a date as "Thu, May 7" (matches the existing site voice)
function formatSessionDate(dt: string | Date, tz: string) {
  return new Date(dt).toLocaleDateString("en-US", {
    weekday: "short", month: "long", day: "numeric", timeZone: tz,
  });
}
function formatSessionTime(start: string | Date, end: string | Date, tz: string) {
  const s = new Date(start), e = new Date(end);
  const fmt: Intl.DateTimeFormatOptions = { hour: "numeric", minute: "2-digit", timeZone: tz };
  const tzShort = new Date(start).toLocaleTimeString("en-US", { timeZone: tz, timeZoneName: "short" }).split(" ").pop();
  return `${s.toLocaleTimeString("en-US", fmt)} – ${e.toLocaleTimeString("en-US", fmt)} ${tzShort ?? ""}`.trim();
}

export function WorkshopHero({
  eyebrow = "FULL-DAY LIVE WORKSHOP",
  headline,
  subtitle,
  kicker,
  proofLines,
  priceLine,
  priceStrike,
  cards,
  sessionCards,
  soldOutCard,
  selectedSessionId,
  onSelectSession,
  primaryCta,
  secondaryCta,
  className = "",
  eyebrowNode,
  hideSessionCardsMobile = false,
  hairlineStops,
}: WorkshopHeroProps) {
  // When the hairline timeline is provided, it supersedes the legacy
  // sessionCards strip — the editorial design owns the date display.
  const hairlineMode = !!hairlineStops && hairlineStops.length > 0;
  const { data: clients } = useFeaturedClientLogos();
  const logos = (clients || []).filter(c => c.logoUrl).slice(0, 12);

  return (
    <section className={`relative bg-cream overflow-hidden ${className}`}>
      {/* Dot matrix background with gradient fade */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none bg-dot-matrix opacity-50"
      />

      {/* Content */}
      <div className="relative z-10">
        {/* Main hero content — centered */}
        <div className="section-padding-y text-center container-content">
          {/* Eyebrow row — 700px wrapper so the row renders at identical width
              on every workshop detail page, with consistent padding below.
              When eyebrowNode is provided (e.g. cowork pulsing dot), render it
              as-is with caller-controlled visibility. Default: WorkshopDetailEyebrow
              hidden on mobile, visible on sm+. */}
          <BlurFade delay={0.1} yOffset={14}>
            <div
              className="mx-auto text-left"
              style={{ width: "100%", maxWidth: 700, marginBottom: 56 }}
            >
              {eyebrowNode ?? (
                <div className="hidden sm:block">
                  <WorkshopDetailEyebrow />
                </div>
              )}
            </div>
          </BlurFade>

          {/* Headline — uses AnimatedHeadline for word-by-word rise on scroll.
              Initial state for each word is y:110%, which renders as clipped
              (not opacity:0), so the h1 still qualifies as an LCP candidate. */}
          <div className="max-w-[var(--max-w-heading)] mx-auto">
            {kicker && <p className="type-eyebrow text-terra mb-4">{kicker}</p>}
            <AnimatedHeadline as="h1" className="type-h1 text-charcoal" text={headline} />
          </div>

          {/* Subtitle */}
          {subtitle && (
            <BlurFade delay={0.3} yOffset={14}>
              <p className="type-body text-charcoal/85 mt-5 max-w-[var(--max-w-text)] mx-auto">
                {subtitle}
              </p>
            </BlurFade>
          )}

          {proofLines && proofLines.length > 0 && (
            <BlurFade delay={0.35} yOffset={14}>
              <ul className="mt-6 max-w-[var(--max-w-text)] mx-auto text-left space-y-2">
                {proofLines.map((l) => (
                  <li key={l.label} className="type-body-sm text-charcoal/85 flex gap-3">
                    <span className="type-label text-terra shrink-0 w-20 pt-0.5">{l.label}</span>
                    <span>{l.text}</span>
                  </li>
                ))}
              </ul>
            </BlurFade>
          )}

          {/* Price — hero-price test only. */}
          {priceLine && (
            <BlurFade delay={0.35} yOffset={14}>
              <p className="type-body font-medium text-charcoal mt-3" data-testid="hero-price">
                {priceStrike && <s className="opacity-60 font-normal mr-2">{priceStrike}</s>}
                {priceLine}
              </p>
            </BlurFade>
          )}

          {/* CTAs */}
          {!hairlineMode && (primaryCta || secondaryCta) && (
            <BlurFade delay={0.4} yOffset={14}>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 sm:gap-4 mt-8 max-w-[380px] sm:max-w-none mx-auto">
                {primaryCta && (
                  <a
                    href={primaryCta.href}
                    className="w-full sm:w-auto"
                    onClick={(e) => {
                      // OPENS THE CHECKOUT (sheet on mobile, drawer on desktop);
                      // anchor-scroll runs only when nothing is bookable (the
                      // rendered sold-out section owns the #tickets anchor then).
                      if (requestCheckout()) e.preventDefault();
                    }}
                  >
                    <button className="btn btn-cta btn-lg w-full sm:w-auto">{primaryCta.label}</button>
                  </a>
                )}
                {secondaryCta && (
                  <a href={secondaryCta.href} className="w-full sm:w-auto">
                    <button className="btn btn-outline btn-lg w-full sm:w-auto">{secondaryCta.label}</button>
                  </a>
                )}
              </div>
            </BlurFade>
          )}

          {/* Hairline scarcity-display timeline — supersedes the legacy
              sessionCards grid when caller provides `hairlineStops`. The
              hairline owns its own desktop + mobile rendering and animation. */}
          {hairlineMode && hairlineStops && (
            <BlurFade delay={0.5} yOffset={14}>
              <div className="container-content mt-12">
                <HairlineTimeline stops={hairlineStops} onSelectSession={onSelectSession} />
              </div>
              {/* Conventional scroll-mouse indicator. Replaces the CTA row that
                  used to live above the timeline — gives prospects a clear
                  "more below" affordance with minimal chrome. */}
              <div className="ht-scroll-cue" aria-hidden="true">
                <div className="ht-scroll-mouse">
                  <div className="ht-scroll-wheel" />
                </div>
              </div>
            </BlurFade>
          )}

          {/* Series mode: charcoal label card + 3 upcoming-date informational cards.
              Suppressed when hairlineMode is active (the hairline replaces it). */}
          {!hairlineMode && sessionCards && sessionCards.length > 0 && (
            <BlurFade delay={0.5} yOffset={14}>
              <div className={`container-breakout mt-12${hideSessionCardsMobile ? " hidden sm:block" : ""}`}>
                <div
                  className="grid grid-cols-1 md:grid-cols-4"
                  style={{ borderTop: "var(--border-structural)", borderLeft: "var(--border-structural)" }}
                >
                  {/* Leftmost label card — charcoal background, wavy mark top-right */}
                  <div
                    className="relative px-6 py-7 md:py-8 flex flex-col items-start justify-end"
                    style={{
                      backgroundColor: "var(--charcoal)",
                      borderRight: "var(--border-structural)",
                      borderBottom: "var(--border-structural)",
                      minHeight: 180,
                    }}
                  >
                    <span
                      className="absolute"
                      style={{ top: 18, right: 18, color: "var(--terra)" }}
                    >
                      <WavyMark size={20} />
                    </span>
                    <span
                      className="text-cream block"
                      style={{
                        fontFamily: "var(--font-display)",
                        fontStyle: "italic",
                        textTransform: "uppercase",
                        textAlign: "left",
                        fontSize: "clamp(22px, 2.6vw, 28px)",
                        fontWeight: 500,
                        lineHeight: 1.1,
                        letterSpacing: "0.02em",
                      }}
                    >
                      Upcoming Dates
                    </span>
                  </div>

                  {soldOutCard && (
                    <div
                      className="relative px-6 py-7 md:py-8 text-center flex flex-col items-center justify-center select-none"
                      style={{
                        backgroundColor: "rgba(217, 64, 0, 0.10)",
                        borderRight: "var(--border-structural)",
                        borderBottom: "var(--border-structural)",
                        minHeight: 180,
                      }}
                      aria-disabled="true"
                    >
                      <span
                        className="absolute"
                        style={{
                          top: 14,
                          right: 14,
                          padding: "3px 8px",
                          backgroundColor: "var(--terra)",
                          color: "var(--cream)",
                          fontFamily: "var(--font-mono)",
                          fontSize: 9,
                          letterSpacing: "0.14em",
                          textTransform: "uppercase",
                          fontWeight: 600,
                        }}
                      >
                        Sold out
                      </span>
                      <span
                        className="block whitespace-nowrap"
                        style={{
                          fontFamily: "var(--font-display)",
                          fontSize: "clamp(20px, 2.4vw, 26px)",
                          fontWeight: 500,
                          lineHeight: 1.15,
                          letterSpacing: "-0.01em",
                          marginBottom: 6,
                          color: "var(--charcoal)",
                          opacity: 0.65,
                          textDecoration: "line-through",
                          textDecorationColor: "rgba(217, 64, 0, 0.6)",
                        }}
                      >
                        {soldOutCard.dateLabel}
                      </span>
                      <span
                        className="block"
                        style={{
                          fontFamily: "var(--font-sans)",
                          fontSize: 15,
                          color: "var(--charcoal-muted)",
                          fontWeight: 400,
                        }}
                      >
                        {soldOutCard.subline ?? "Sold out — see open dates"}
                      </span>
                    </div>
                  )}

                  {sessionCards.map((s) => {
                    // Tiles flagged soldOut render inline with the sold-out
                    // styling so the strip stays in true chronological order
                    // (rather than hoisting sold-outs into a single dedicated
                    // slot before bookable dates).
                    if (s.soldOut) {
                      return (
                        <div
                          key={s.id}
                          className="relative px-6 py-7 md:py-8 text-center flex flex-col items-center justify-center select-none"
                          style={{
                            backgroundColor: "rgba(217, 64, 0, 0.10)",
                            borderRight: "var(--border-structural)",
                            borderBottom: "var(--border-structural)",
                            minHeight: 180,
                          }}
                          aria-disabled="true"
                        >
                          <span
                            className="absolute"
                            style={{
                              top: 14,
                              right: 14,
                              padding: "3px 8px",
                              backgroundColor: "var(--terra)",
                              color: "var(--cream)",
                              fontFamily: "var(--font-mono)",
                              fontSize: 9,
                              letterSpacing: "0.14em",
                              textTransform: "uppercase",
                              fontWeight: 600,
                            }}
                          >
                            Sold out
                          </span>
                          <span
                            className="block whitespace-nowrap"
                            style={{
                              fontFamily: "var(--font-display)",
                              fontSize: "clamp(20px, 2.4vw, 26px)",
                              fontWeight: 500,
                              lineHeight: 1.15,
                              letterSpacing: "-0.01em",
                              marginBottom: 6,
                              color: "var(--charcoal)",
                              opacity: 0.65,
                              textDecoration: "line-through",
                              textDecorationColor: "rgba(217, 64, 0, 0.6)",
                            }}
                          >
                            {formatSessionDate(s.startDatetime, s.timezone)}
                          </span>
                          <span
                            className="block"
                            style={{
                              fontFamily: "var(--font-sans)",
                              fontSize: 15,
                              color: "var(--charcoal-muted)",
                              fontWeight: 400,
                            }}
                          >
                            Sold out — see open dates
                          </span>
                        </div>
                      );
                    }
                    const cardBody = (
                      <>
                        <span
                          className="text-charcoal block whitespace-nowrap"
                          style={{
                            fontFamily: "var(--font-display)",
                            fontSize: "clamp(20px, 2.4vw, 26px)",
                            fontWeight: 500,
                            lineHeight: 1.15,
                            letterSpacing: "-0.01em",
                            marginBottom: 6,
                          }}
                        >
                          {formatSessionDate(s.startDatetime, s.timezone)}
                        </span>
                        <span
                          className="block whitespace-nowrap"
                          style={{
                            fontFamily: "var(--font-sans)",
                            fontSize: 17,
                            color: "var(--charcoal-muted)",
                            fontWeight: 400,
                            marginBottom: 4,
                          }}
                        >
                          {formatSessionTime(s.startDatetime, s.endDatetime, s.timezone)}
                        </span>
                        <span
                          className="block whitespace-nowrap"
                          style={{
                            fontFamily: "var(--font-sans)",
                            fontSize: 17,
                            color: "var(--charcoal-muted)",
                            fontWeight: 400,
                          }}
                        >
                          Virtual — Zoom
                        </span>
                      </>
                    );
                    // Bookable cards are buttons: clicking selects the session
                    // and (via the caller) scrolls to the ticketing block.
                    if (onSelectSession) {
                      return (
                        <button
                          key={s.id}
                          type="button"
                          onClick={() => onSelectSession(s.id)}
                          className="px-6 py-7 md:py-8 text-center flex flex-col items-center justify-center cursor-pointer transition-colors hover:bg-charcoal/[0.04] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px]"
                          style={{
                            borderRight: "var(--border-structural)",
                            borderBottom: "var(--border-structural)",
                            minHeight: 180,
                          }}
                          aria-label={`Reserve seat for ${formatSessionDate(s.startDatetime, s.timezone)}`}
                        >
                          {cardBody}
                        </button>
                      );
                    }
                    return (
                    <div
                      key={s.id}
                      className="px-6 py-7 md:py-8 text-center flex flex-col items-center justify-center"
                      style={{
                        borderRight: "var(--border-structural)",
                        borderBottom: "var(--border-structural)",
                        minHeight: 180,
                      }}
                    >
                      {cardBody}
                    </div>
                    );
                  })}
                </div>
              </div>
            </BlurFade>
          )}

          {/* Legacy single-event info cards (DATE / TIME / FORMAT) */}
          {!sessionCards && cards && cards.length > 0 && (
            <BlurFade delay={0.5} yOffset={14}>
              <div className="container-breakout mt-12">
                <div className="grid grid-cols-1 md:grid-cols-3">
                  {cards.map((card, i) => (
                    <div
                      key={i}
                      className="px-8 py-8 md:py-10 text-center flex flex-col items-center justify-center"
                    >
                      {card.icon && (
                        <img
                          src={card.icon}
                          alt=""
                          aria-hidden="true"
                          className="w-14 h-14 md:w-16 md:h-16 mb-3"
                          width={400}
                          height={400}
                          loading="lazy"
                        />
                      )}
                      <span className="type-label text-charcoal/75 block mb-2">{card.label}</span>
                      <span className="type-h4-sm text-charcoal whitespace-nowrap">{card.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </BlurFade>
          )}
        </div>
      </div>
    </section>
  );
}
